import { useState, useCallback } from 'react';

interface ApiCallOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
  headers?: Record<string, string>;
  /** Сколько раз повторить запрос при СЕТЕВОЙ ошибке. Только для идемпотентных запросов. */
  retries?: number;
  /** Пауза между повторами, мс (по умолчанию 800). */
  retryDelayMs?: number;
  /** Таймаут запроса, мс (по умолчанию 20000). */
  timeoutMs?: number;
}

interface ApiCallResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (options?: Partial<ApiCallOptions>) => Promise<T | null>;
}

/** Сессия действительно мертва (код 2003 от шлюза). Только по ней можно удалять токен. */
export class SessionExpiredError extends Error {
  code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = 'SessionExpiredError';
    this.code = code;
  }
}

/**
 * Связи с сервером нет: обрыв сети, таймаут, отменённый браузером запрос, 5xx, не-JSON ответ.
 * Такая ошибка НИЧЕГО не говорит о валидности токена — инвалидировать сессию по ней нельзя.
 */
export class NetworkError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'NetworkError';
    this.status = status;
  }
}

/** Сервер ответил осмысленным отказом (4xx, кроме протухшей сессии). */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const DEFAULT_TIMEOUT_MS = 20000;
const DEFAULT_RETRY_DELAY_MS = 800;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const requestOnce = async (options: ApiCallOptions): Promise<any> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(options.url, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
      signal: controller.signal,
    });
  } catch (err: any) {
    // fetch реджектится на обрыве сети и на отмене. Safari на iOS рвёт запросы,
    // когда вкладка уходит в фон (в частности — на прыжке в приложение ВК и обратно).
    throw new NetworkError(
      err?.name === 'AbortError'
        ? 'Превышено время ожидания ответа сервера'
        : 'Нет связи с сервером',
    );
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let errorData: any = null;
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }

    // Протухшая сессия — единственный случай, когда токен можно считать мёртвым.
    if (errorData?.code === 2003 || (typeof errorData?.message === 'string' && errorData.message.includes('SESSION_EXPIRED'))) {
      throw new SessionExpiredError(errorData?.message || 'Сессия истекла', errorData?.code || 2003);
    }

    // 5xx/408/429 — временная недоступность, имеет смысл повторить.
    if (response.status >= 500 || response.status === 408 || response.status === 429) {
      throw new NetworkError(
        errorData?.message || `Сервер временно недоступен (${response.status})`,
        response.status,
      );
    }

    throw new ApiError(errorData?.message || `HTTP error! status: ${response.status}`, response.status);
  }

  try {
    return await response.json();
  } catch {
    // 200, но тело не JSON — это ответ прокси/CDN/капхот-портала, а не API.
    throw new NetworkError('Некорректный ответ сервера');
  }
};

export const useApiCall = <T = any>(defaultOptions?: ApiCallOptions): ApiCallResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * ВАЖНО: при любой неудаче бросает исключение (раньше — молча возвращал null,
   * из-за чего вызывающий код принимал сетевой сбой за «пользователь не найден»
   * и удалял живой токен). Все вызовы обёрнуты в try/catch.
   */
  const execute = useCallback(async (options: Partial<ApiCallOptions> = {}): Promise<T | null> => {
    const finalOptions = { ...defaultOptions, ...options } as ApiCallOptions;

    if (!finalOptions.url) {
      throw new Error('URL is required');
    }

    const retries = finalOptions.retries ?? 0;
    const retryDelayMs = finalOptions.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

    setLoading(true);
    setError(null);

    try {
      let attempt = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const result = await requestOnce(finalOptions);
          setData(result);
          return result;
        } catch (err) {
          // Повторяем только сетевые сбои: отказ сервера и протухшая сессия не «вылечатся».
          if (err instanceof NetworkError && attempt < retries) {
            attempt += 1;
            console.warn(`[api] Сетевая ошибка, повтор ${attempt}/${retries}:`, err.message);
            await wait(retryDelayMs);
            continue;
          }
          throw err;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [defaultOptions]);

  return {
    data,
    loading,
    error,
    execute,
  };
};
